import _ from 'lodash';
import React from 'react';
import { navigateAction } from 'fluxible-router';
import shallowCompare from '../../lib/shallowCompare';
import Store from '../../models/Store';
import InventoryRecordActions from '../../actions/InventoryRecordActions';
import { defaultSorter } from '../../stores/InventoryRecordStore';
import InventoryRecordsList from '../InventoryRecordsList';
import UniversalSearchResultsList from '../UniversalSearchResultsList';
import Select from '../Select';
import AttributedText from '../AttributedText';
import PriceFilterSlider from '../PriceFilterSlider';
import { toTitleCase } from '../../lib/utils';
import { trackSearchEvent } from '../InventorySearch';
import TagDeletePng from '../images/tag_delete.png';
import TagDeleteSvg from '../images/tag_delete.svg';
import styles from './index.sass';

const SorterOptions = [
  {value: 'rating:desc', label: 'Rating'},
  {value: 'price:asc', label: 'Price Low to High'},
  {value: 'price:desc', label: 'Price High to Low'},
  {value: 'created_at:desc', label: 'Recently Added'}
];
const WidthsBySortKey = {
  'rating': 73,
  'price': 159,
  'created_at': 144
};
const SearchUIMethodsBySorter = {
  'rating:desc': 'sort_ratings',
  'price:asc': 'sort_price_low_to_high',
  'price:desc': 'sort_price_high_to_low',
  'created_at:desc': 'sort_recently_added'
};

class InventorySearchResults extends React.Component {
  static propTypes = {
    store: React.PropTypes.instanceOf(Store),
    pagination: React.PropTypes.object,
    filters: React.PropTypes.array,
    sorter: React.PropTypes.object,
    suggestedFilters: React.PropTypes.array,
    inventoryRecords: React.PropTypes.object,
    wineSkus: React.PropTypes.object, // Either inventoryRecords OR wineSku should be provided
    loading: React.PropTypes.bool,
    currentRoute: React.PropTypes.object,
    title: React.PropTypes.string
  };

  static contextTypes = {
    executeAction: React.PropTypes.func,
    getStore: React.PropTypes.func,
    trackEvent: React.PropTypes.func,
    buildURLForRoute: React.PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      priceFilters: this.getPriceFilters(props.filters),
      sorter: this.props.sorter,
      loading: props.loading,
      requestedOffset: 0
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillReceiveProps(nextProps) {
    if(nextProps.filters !== this.props.filters) {
      this.setState({priceFilters: this.getPriceFilters(nextProps.filters)});
    }
    if(nextProps.sorter !== this.props.sorter) {
      this.setState({sorter: nextProps.sorter});
    }
    if(nextProps.filters !== this.props.filters || nextProps.sorter !== this.props.sorter) {
      this.setState({requestedOffset: 0});
    }
    if(nextProps.loading !== this.props.loading) {
      this.setState({loading: nextProps.loading});
    }
  }

  getPriceFilters(filters) {
    let minPrice, maxPrice, priceFilters = [];
    filters.forEach(filter => {
      if(filter.type === 'min_price_in_cents') {
        minPrice = filter;
      } else if(filter.type === 'max_price_in_cents') {
        maxPrice = filter;
      }
    });
    if(minPrice) { priceFilters.push(minPrice); }
    if(maxPrice) { priceFilters.push(maxPrice); }
    return priceFilters;
  }

  buildUrlAndNavigate(params) {
    let { currentRoute } = this.props;
    let url = this.context.buildURLForRoute(currentRoute.name, {
      type: currentRoute.params.type,
      filters: currentRoute.params.filters ? params.filters.concat(currentRoute.params.filters) : params.filters,
      sorter: (!params.sorter || _.isEqual(params.sorter, defaultSorter)) ? null : params.sorter
    });
    this.context.executeAction(navigateAction, {url});
  }

  handleOnClickFilter(filter) {
    // Remove it
    let filters = _.filter(this.props.filters, (f) => {
      return !_.isEqual(f, filter);
    });
    trackSearchEvent(this.context, {
      filters: filters,
      pagination: this.props.pagination,
      sorter: this.props.sorter,
      uiMethod: 'delete_token'
    });

    this.buildUrlAndNavigate({
      filters: filters,
      pagination: this.props.pagination,
      sorter: this.props.sorter
    });
  }

  handleOnClickSuggestedFilter(filter) {
    let filters = this.props.filters.concat([filter]);
    trackSearchEvent(this.context, {
      filters: filters,
      pagination: this.props.pagination,
      sorter: this.props.sorter,
      uiMethod: 'tag_suggested'
    });
    this.buildUrlAndNavigate({
      filters: filters,
      pagination: this.props.pagination,
      sorter: this.props.sorter
    });
  }

  handleOnChangeSortBySelect(value) {
    let [key, direction] = value.split(':');
    let sorter = {key, direction};
    this.setState({sorter});
    trackSearchEvent(this.context, {
      filters: this.props.filters,
      pagination: this.props.pagination,
      sorter: sorter,
      uiMethod: SearchUIMethodsBySorter[value]
    });
    this.buildUrlAndNavigate({
      filters: this.props.filters,
      pagination: this.props.pagination,
      sorter: sorter
    });
  }

  handleOnChangePriceFilterSlider(priceFilters) {
    let filters = _.filter(this.props.filters, (filter) => {
      return filter.type !== 'min_price_in_cents' && filter.type !== 'max_price_in_cents';
    });
    trackSearchEvent(this.context, {
      filters: filters,
      pagination: this.props.pagination,
      sorter: this.props.sorter,
      uiMethod: 'price_filter'
    });
    this.buildUrlAndNavigate({
      filters: filters.concat(priceFilters),
      pagination: this.props.pagination,
      sorter: this.props.sorter
    });
  }

  handleOnClickLoadMoreButton(e) {
    let offset = this.props.pagination.offset + this.props.pagination.limit;
    if(offset <= this.state.requestedOffset) {return;}
    this.setState({
      loading: true,
      requestedOffset: offset
    });
    this.context.executeAction(InventoryRecordActions.list, {
      filters: this.props.filters,
      sorter: this.props.sorter,
      pagination: _.extend({}, this.props.pagination, {offset: offset})
    });
  }

  formatFilterValue(filter) {
    let formattedValue = filter.value.toString();
    switch(filter.type) {
      case 'tag':
      case 'color':
        formattedValue = toTitleCase(formattedValue.replace(/-/g, ' '));
        break;
      case 'region':
      case 'varietal':
      default:
        break;
    }
    return formattedValue;
  }

  renderSuggestedFilters() {
    let filters = [];
    let categoryRouteParams = this.props.currentRoute.params;

    _.each(this.props.filters, (filter, i) => {
      if(filter.type === 'text' || filter.type === 'min_price_in_cents' || filter.type === 'max_price_in_cents' || filter.type === 'store_id') {return;}
      if(_.find(categoryRouteParams.filters, (f) => {return _.isEqual(f, filter);})) {return;}
      let buttonProps = {
        className: `${styles.filter} ${styles.applied}`,
        key: this.props.suggestedFilters.length + i,
        onClick: this.handleOnClickFilter.bind(this, filter)
      };
      filters.push(
        <button {...buttonProps}>
          <span className={styles.value}>{this.formatFilterValue(filter)}</span>
          <img src={TagDeletePng} srcSet={TagDeleteSvg} />
        </button>
      );
    });

    _.each(this.props.suggestedFilters, (suggestedFilter, i) => {
      if(_.find(this.props.filters, (filter) => {return _.isEqual(filter, suggestedFilter.filter);})) {return;}
      let buttonProps = {
        className: styles.filter,
        key: i,
        onClick: this.handleOnClickSuggestedFilter.bind(this, suggestedFilter.filter)
      };
      filters.push(
        <button {...buttonProps}>
          <span className={styles.value}>{this.formatFilterValue(suggestedFilter.filter)}</span>
          <span className={styles.count}>{suggestedFilter.results}</span>
        </button>
      );
    });

    if(!filters.length) {return null;}

    return (
      <div className={styles.suggestedFiltersFilterOption}>
        <label className={styles.label}>Filters</label>
        <div className={styles.filterListWrapper}>
          <div className={styles.filterList}>
            {filters}
          </div>
        </div>
      </div>
    );
  }

  renderSortBySelectOptions() {
    return SorterOptions.map((sorter) => {
      return <option value={sorter.value} key={sorter.value}>{sorter.label}</option>;
    });
  }

  renderResults() {
    let { inventoryRecords, wineSkus, store, pagination, loading } = this.props;
    let resultsList;
    let loadMoreButton;
    if(store) {
      let inventoryRecordsListProps = {
        inventoryRecords,
        loading,
        storeId: store.id,
        emptyText: 'No results. Try modifying your search.'
      };
      resultsList = <InventoryRecordsList {...inventoryRecordsListProps} />;
      if(!this.state.loading && inventoryRecords.size && inventoryRecords.size < pagination.totalResults) {
        let loadMoreButtonProps = {
          className: styles.loadMoreButton,
          onClick: this.handleOnClickLoadMoreButton.bind(this)
        };
        loadMoreButton = <button {...loadMoreButtonProps}>See more</button>;
      }
    } else {
      // Universal
      let universalSearchResultsListProps = {
        wineSkus,
        loading,
        emptyText: 'No results. Try modifying your search.'
      };
      resultsList = <UniversalSearchResultsList {...universalSearchResultsListProps} />;
    }

    return (
      <div className={styles.inventoryRecordsList}>
        {resultsList}
        {loadMoreButton}
      </div>
    );
  }

  renderSortBySelect() {
    let { pagination, store } = this.props;
    if(!store) {return null;}
    let { sorter } = this.state;
    let searchResultCount = pagination && pagination.hasOwnProperty('totalResults') ? pagination.totalResults : null;
    let sortByPretext = null;
    if(searchResultCount !== null) {
      if(!searchResultCount) {
        sortByPretext = <span className={styles.sortByPretext}>No results</span>;
      } else if(searchResultCount === 1) {
        sortByPretext = <span className={styles.sortByPretext}>1 result <span className={styles.light}>Sorted by</span></span>;
      } else {
        sortByPretext = <span className={styles.sortByPretext}>{searchResultCount} results <span className={styles.light}>Sorted by</span></span>;
      }
    }
    let sortBySelectProps = {
      className: styles.sortBySelect,
      value: `${sorter.key}:${sorter.direction}`,
      onChange: this.handleOnChangeSortBySelect.bind(this),
      style: {
        width: WidthsBySortKey[sorter.key] // Select elements can't resize by selected value
      }
    };
    return (
      <div className={styles.filterOption}>
        <label className={styles.label}>Sort By</label>
        {sortByPretext}
        <Select {...sortBySelectProps}>
          {this.renderSortBySelectOptions()}
        </Select>
      </div>
    );
  }

  renderPriceFilterSlider() {
    let { store } = this.props;
    let { priceFilters } = this.state;
    let priceFilterSliderProps = {
      minPriceInCents: store ? store.min_price_in_cents : 0,
      maxPriceInCents: store ? store.max_price_in_cents : 60000,
      filters: priceFilters,
      onChange: this.handleOnChangePriceFilterSlider.bind(this)
    };
    return (
      <div className={styles.filterOption}>
        <label className={styles.label}>Price Range</label>
        <PriceFilterSlider {...priceFilterSliderProps} />
      </div>
    );
  }

  render() {
    let { title } = this.props;
    return (
      <div className={styles.searchResults}>
      {title ? <h1 className={styles.title}>{title}</h1> : null}
        <div className={styles.table}>
          <div className={styles.filterOptions}>
            {this.renderSortBySelect()}
            {this.renderPriceFilterSlider()}
            {this.renderSuggestedFilters()}
          </div>
          {this.renderResults()}
        </div>
      </div>
    );
  }
}

export default InventorySearchResults;



// WEBPACK FOOTER //
// banquet/components/InventorySearchResults/index.js